import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DatePicker, Modal } from 'antd';
import moment from 'moment';
import {
	formatComparisonRangeLabel,
	matchDurationFromEnd,
	matchDurationFromStart,
} from '../../utils/AnalyticsComparisonUtils';

interface Props {
	open: boolean;
	onClose: () => void;
	onApply: (start: moment.Moment, end: moment.Moment) => void;
	selectedStart: moment.Moment | null;
	selectedEnd: moment.Moment | null;
	isTimeBased: boolean;
	initialStart: moment.Moment | null;
	initialEnd: moment.Moment | null;
}

const DATE_FORMAT = 'MM-DD-YYYY';

const CustomRangeModal = ({
	open,
	onClose,
	onApply,
	selectedStart,
	selectedEnd,
	isTimeBased,
	initialStart,
	initialEnd,
}: Props) => {
	const [start, setStart] = useState<moment.Moment | null>(initialStart);
	const [end, setEnd] = useState<moment.Moment | null>(initialEnd);

	useEffect(() => {
		if (open) {
			setStart(initialStart);
			setEnd(initialEnd);
		}
	}, [open, initialStart, initialEnd]);

	const durationDays = useMemo(() => {
		if (!selectedStart || !selectedEnd) return 0;
		return selectedEnd.diff(selectedStart, 'days');
	}, [selectedStart, selectedEnd]);

	// Disallow comparing against the future — sparse/empty data would mislead.
	const disabledDate = useCallback(
		(date: moment.Moment | null) => !!date && date.isAfter(moment().endOf('day')),
		[]
	);

	const clampToToday = useCallback((date: moment.Moment) => {
		const today = moment().endOf('day');
		return date.isAfter(today) ? today.clone().startOf('day') : date;
	}, []);

	const handleStartChange = useCallback(
		(nextStart: moment.Moment | null) => {
			if (!nextStart) {
				setStart(null);
				return;
			}
			if (isTimeBased && selectedStart && selectedEnd) {
				const [nextS, nextE] = matchDurationFromStart(nextStart, selectedStart, selectedEnd);
				// In time-locked mode the auto-generated end date can land in the
				// future when the user picks a start close to today. Clamp it back
				// to today so we never request a partially-future comparison range.
				const clampedEnd = clampToToday(nextE);
				setStart(nextS);
				setEnd(clampedEnd);
				return;
			}
			setStart(nextStart);
		},
		[isTimeBased, selectedStart, selectedEnd, clampToToday]
	);

	const handleEndChange = useCallback(
		(nextEnd: moment.Moment | null) => {
			if (!nextEnd) {
				setEnd(null);
				return;
			}
			if (isTimeBased && selectedStart && selectedEnd) {
				const [nextS, nextE] = matchDurationFromEnd(nextEnd, selectedStart, selectedEnd);
				const clampedEnd = clampToToday(nextE);
				setStart(nextS);
				setEnd(clampedEnd);
				return;
			}
			setEnd(nextEnd);
		},
		[isTimeBased, selectedStart, selectedEnd, clampToToday]
	);

	const canApply = !!(start && end && end.isSameOrAfter(start));

	const handleApply = useCallback(() => {
		if (!canApply || !start || !end) return;
		onApply(start, end);
	}, [canApply, start, end, onApply]);

	const subtitle = useMemo(() => {
		if (isTimeBased) {
			return `Comparison range is locked to ${durationDays + 1} days to match the selected period.`;
		}
		if (start && end) {
			const total = end.diff(start, 'days') + 1;
			return `Comparing against: ${formatComparisonRangeLabel(start, end)} (${total} days)`;
		}
		return 'Select any start and end date.';
	}, [isTimeBased, durationDays, start, end]);

	return (
		<Modal
			title="Select Custom Range"
			visible={open}
			onCancel={onClose}
			onOk={handleApply}
			okButtonProps={{ disabled: !canApply }}
			okText="Apply"
			cancelText="Cancel"
			destroyOnClose
		>
			<div className="flex flex-col gap-3">
				<div className="flex flex-col gap-1">
					<label className="footnoteMedium text-character-secondary">Start date</label>
					<DatePicker
						value={start}
						onChange={handleStartChange}
						format={DATE_FORMAT}
						allowClear
						disabledDate={disabledDate}
						className="w-full"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label className="footnoteMedium text-character-secondary">End date</label>
					<DatePicker
						value={end}
						onChange={handleEndChange}
						format={DATE_FORMAT}
						allowClear
						disabledDate={disabledDate}
						className="w-full"
					/>
				</div>
				<p className="footnoteregular m-0 text-character-secondary">{subtitle}</p>
			</div>
		</Modal>
	);
};

export default CustomRangeModal;
